/**
 * Tiny inline-SVG vital widgets for the rich network/room tiles (LANDING).
 *
 * Deliberately NO chart lib (recharts/visx) — each widget is a handful of SVG
 * elements, so the tiles stay light and the bundle lean. framer-motion (already
 * a dep) drives the "live" pulse. All widgets degrade gracefully on null/absent
 * data (show "—", never a fake 0%).
 */
import React from 'react';
import { motion } from 'framer-motion';

/** Format a points number compactly: 1234 → "1.2k", 1500000 → "1.5M". */
export function fmtPoints(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(Math.round(v));
}

/** Compact USD label. Sub-cent spend keeps precision so a real run never reads
 *  "$0.00"; ≥$1 shows 2 decimals. USD only (billing copy rule). */
export function fmtUsd(n) {
  const x = Number(n) || 0;
  if (x > 0 && x < 0.01) return `$${x.toFixed(4)}`;
  return `$${x.toFixed(2)}`;
}

/** A small labelled stat: icon + value + label. `value` may be a node. */
export function StatChip({ icon: Icon, value, label, tone = 'gray', title }) {
  const toneCls = {
    gray: 'text-gray-700',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    indigo: 'text-indigo-700',
  }[tone] || 'text-gray-700';
  return (
    <div className="flex items-center gap-1.5" title={title}>
      {Icon ? <Icon className={`h-3.5 w-3.5 shrink-0 ${toneCls}`} aria-hidden /> : null}
      <span className="text-xs font-semibold text-gray-900 tabular-nums">{value}</span>
      {label ? <span className="text-[10px] text-gray-500">{label}</span> : null}
    </div>
  );
}

/**
 * A compact success-rate ring (0-100). `pct` null → a neutral dashed ring + "—".
 * Green ≥90, amber ≥70, red below. ~28px.
 */
export function SuccessRing({ pct, size = 28 }) {
  const has = typeof pct === 'number' && !Number.isNaN(pct);
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  const clamped = has ? Math.max(0, Math.min(100, pct)) : 0;
  const dash = has ? (clamped / 100) * c : 0;
  const color = !has ? '#d1d5db' : clamped >= 90 ? '#059669' : clamped >= 70 ? '#d97706' : '#dc2626';
  return (
    <div className="relative inline-flex items-center justify-center" title={has ? `${clamped}% success` : 'No runs yet'}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="3"
          strokeDasharray={has ? 'none' : '2 3'} />
        {has ? (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3"
            strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`} />
        ) : null}
      </svg>
      <span className="absolute text-[8px] font-bold text-gray-700 tabular-nums">
        {has ? `${Math.round(clamped)}` : '—'}
      </span>
    </div>
  );
}

/**
 * Consumption bar. When `budget` (>0) is given → a filled bar (consumed/budget)
 * that turns amber ≥80% and red ≥100%. Without a budget → just the consumed
 * points, no bar (budget is optional per the spec).
 */
// ``hideUnit`` — drop the meter's own "pts" suffix when the caller already shows
// a unit selector next to it (tile + footer dropdown), so it doesn't read "0pts pts".
// Standalone callers (RoomStatRow) leave it false and keep the "pts" label.
export function ConsumptionMeter({ consumed, budget, unit = 'usd', consumedUsd, hideUnit = false }) {
  const c = Number(consumed || 0);
  const b = Number(budget || 0);
  // USD mode: show the $ figure. There is no $ budget (allocations are in points),
  // so no ratio bar here — the bare cost is the signal. Falls back to points if
  // no usd value is present (older payloads).
  if (unit === 'usd' && consumedUsd != null) {
    return (
      <div className="flex items-center gap-1" title={`${fmtUsd(consumedUsd)} consumed (est.)`}>
        <span className="text-xs font-semibold text-gray-900 tabular-nums">{fmtUsd(consumedUsd)}</span>
      </div>
    );
  }
  if (b > 0) {
    const ratio = Math.max(0, Math.min(1.2, c / b)); // allow slight overflow render
    const pct = Math.round((c / b) * 100);
    const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500';
    return (
      <div className="min-w-0 flex-1" title={`${fmtPoints(c)} / ${fmtPoints(b)} pts (${pct}%)`}>
        <div className="flex items-center justify-between text-[10px] text-gray-500">
          <span className="tabular-nums text-gray-700">{fmtPoints(c)}</span>
          <span className="tabular-nums">/ {fmtPoints(b)}</span>
        </div>
        <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1" title={`${fmtPoints(c)} points consumed`}>
      <span className="text-xs font-semibold text-gray-900 tabular-nums">{fmtPoints(c)}</span>
      {hideUnit ? null : <span className="text-[10px] text-gray-500">pts</span>}
    </div>
  );
}

/** Live pulse dot + count when >0 active; a still gray dot + "0" otherwise. */
export function ActivePulse({ count }) {
  const n = Number(count || 0);
  const live = n > 0;
  return (
    <div className="flex items-center gap-1.5" title={live ? `${n} room${n === 1 ? '' : 's'} running` : 'Idle'}>
      <span className="relative inline-flex h-2 w-2">
        {live ? (
          <motion.span
            className="absolute inline-flex h-full w-full rounded-full bg-emerald-400"
            animate={{ opacity: [0.7, 0, 0.7], scale: [1, 2.2, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        ) : null}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300'}`} />
      </span>
      <span className={`text-xs font-semibold tabular-nums ${live ? 'text-emerald-700' : 'text-gray-500'}`}>{n}</span>
    </div>
  );
}

/** Outcome (room-context %) chip — only render when a number is present. */
export function OutcomeChip({ pct }) {
  if (typeof pct !== 'number' || Number.isNaN(pct)) return null;
  const v = Math.round(Math.max(0, Math.min(100, pct)));
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
      title={`Room context ~${v}% accumulated`}
    >
      ◐ {v}%
    </span>
  );
}
